/**
 * Users API Client
 * Epic 2: User Management
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type UserRole =
  | 'OPERATOR'
  | 'TECHNIKUS'
  | 'BOLTVEZETO'
  | 'ACCOUNTANT'
  | 'PARTNER_OWNER'
  | 'CENTRAL_ADMIN'
  | 'DEVOPS_ADMIN'
  | 'SUPER_ADMIN';

export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'LOCKED' | 'PENDING_VERIFICATION';

export interface UserResponse {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenantId: string;
  locationId?: string | null;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
}

export interface UserListResponse {
  data: UserResponse[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface SingleUserResponse {
  data: UserResponse;
}

export interface UserFilters {
  search?: string;
  role?: UserRole;
  status?: UserStatus;
  locationId?: string;
  limit?: number;
  offset?: number;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of users with filters
 */
export async function getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.role) params.append('role', filters.role);
  if (filters.status) params.append('status', filters.status);
  if (filters.locationId) params.append('locationId', filters.locationId);
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/users?${queryString}` : '/users';

  return api.get<UserListResponse>(endpoint);
}

/**
 * Get single user by ID
 */
export async function getUserById(id: string): Promise<SingleUserResponse> {
  return api.get<SingleUserResponse>(`/users/${id}`);
}

/**
 * Create new user
 */
export async function createUser(data: {
  email: string;
  name: string;
  password: string;
  role: UserRole;
  locationId?: string;
}): Promise<SingleUserResponse> {
  return api.post<SingleUserResponse>('/users', data);
}

/**
 * Update user
 */
export async function updateUser(
  id: string,
  data: {
    name?: string;
    email?: string;
    role?: UserRole;
    status?: UserStatus;
    locationId?: string;
  }
): Promise<SingleUserResponse> {
  return api.patch<SingleUserResponse>(`/users/${id}`, data);
}

/**
 * Delete user (soft delete)
 */
export async function deleteUser(
  id: string
): Promise<{ data: { success: boolean; message: string } }> {
  return api.delete<{ data: { success: boolean; message: string } }>(`/users/${id}`);
}
